/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useContext, useState, useEffect } from 'react';

import { withRouter, matchPath } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import getSymbolFromCurrency from 'currency-symbol-map';
import Config from 'config';

import { injectIntl } from 'react-intl';

import { Dropdown } from 'semantic-ui-react';
import { NEW_DASHBOARD_RELEASE_DATE } from 'src/core/release-dates/constants';

import membershipsQuery from './memberships.gql';

import { trackAmplitude } from '../../lib/analytics';

import locale from '../account-navbar/locale';

import { OrganizationContext } from '../organization-select/withOrganization';

import DashboardIcon from '../../assets/account-nav-bar/dashboard.svg';
import DashboardIconActive from '../../assets/account-nav-bar/dashboard_active.svg';
import MyEventsIcon from '../../assets/account-nav-bar/my_events.svg';
import MyEventsIconActive from '../../assets/account-nav-bar/my_events_active.svg';
import ReportsIcon from '../../assets/account-nav-bar/reports.svg';
import ReportsIconActive from '../../assets/account-nav-bar/reports_active.svg';
import GDCIcon from '../../assets/account-nav-bar/gdc_inactive.svg';
import GDCIconActive from '../../assets/account-nav-bar/gdc_active.svg';
import AddOnsIcon from '../../assets/account-nav-bar/discount.svg';
import AddOnsActiveIcon from '../../assets/account-nav-bar/discount_active.svg';
import ChevronIcon from '../../assets/account-nav-bar/chevron.svg';
import NewFeatureBadgeWithExpiry from '../utility/NewFeatureBadgeWithExpiry';
import BalancesMobileIcon from '../../assets/account-nav-bar/balances_mobile.svg';
import HandCoinIcon from '../../assets/account-nav-bar/hand_coin.svg';
import FeesIcon from '../../assets/account-nav-bar/fees.svg';
import FeesIconActive from '../../assets/account-nav-bar/fees_active.svg';
import OrganizationSelect from '../organization-select';
import useFeatureFlags from '../../hooks/use-feature-flags';
import styles from './styles.css';

const DASHBOARD = '/dashboard';
const NEW_DASHBOARD = '/dashboard'; // TODO: Remove this constant Novemeber 2023.
const MY_EVENTS = '/dashboard/my-events';
const REPORTS = '/dashboard/reports';
const DISCOUNT_CODES = '/dashboard/discount-codes';
const ADD_ONS = '/dashboard/add-ons';
const ADD_ON_ORDERS = '/dashboard/add-ons/orders';
const ADD_ON_CREATE = '/dashboard/add-ons/create';
const ADD_ON_EDIT = '/dashboard/add-ons/edit/:id';
const BALANCES = '/dashboard/balance';
const ADD_ONS_NEW = '/dashboard/add-ons';

const FEES = '/host/fees';

const AccountNavbar = (props) => {

  const currentRoute = props.location.pathname;

  const { selectedMembership, selectedUser } = useContext(OrganizationContext);
  const [ expanded, setExpanded ] = useState(false);
  const [ permissions, setPermissions ] = useState({});
  const [ isDesktopLayout, setIsDesktopLayout ] = useState(
    window.matchMedia("(min-width: 992px)").matches
  )
  const { data, loading, error } = useQuery(membershipsQuery);
  const user = props.currentUser
  const formatMessage = (message) => props.intl.formatMessage(message);
  const { featureFlags } = useFeatureFlags(user.id)

  function getMemberships() {
    return !loading && !error && data && data.viewer.memberships.nodes
      ? data.viewer.memberships.nodes : [];
  }

  const handleTrackAmplitude = (eventName, eventData) => {
    trackAmplitude({
      eventName,
      eventData,
      user
    });
  }

  // Disable organization select if we're on the edit add-ons page.
  const organizationSelectionEnabled = !matchPath(currentRoute, {
    path: '/dashboard/add-ons/edit/:id',
    exact: false,
    strict: false
  });

  useEffect(() => {
    window.matchMedia("(min-width: 992px)")
    .addEventListener('change', e => setIsDesktopLayout( e.matches ));

    return () =>
      window.matchMedia("(max-width: 992px)")
      .removeEventListener('change', e => setIsDesktopLayout( e.matches ));
  });

  useEffect(() => {
    const membership = selectedMembership;

    if (membership == null) return

    setPermissions({
      hasPermissionForReports: membership.report,
      hasPermissionForDashboard: membership.manage_overview,
      hasPermissionForManageAddOns: membership.manage_add_ons,
      hasPermissionForDiscountCodes: (membership.all_listings && membership.promote),
      hasPermissionForFees: featureFlags?.hostAddedFeePresets && membership.manage_fee_presets,
      })
  }, [selectedMembership, featureFlags]);

  const isCurrentRouteDashboard = () => {
    return currentRoute === DASHBOARD || currentRoute === NEW_DASHBOARD
  };
  const isCurrentRouteFees = () => currentRoute === FEES

  const toggleShowOptions = async () => setExpanded(!expanded);

  const selectedRoute = (Icon, title) => {
    return (
      <div className={styles.accountNavbarSelected}>
        <div className={styles.accountNavbarIcon}>
          <Icon />
        </div>
        {title}
      </div>
    );
  }

  const isRouteMatched = (route) => {
    return matchPath(currentRoute, {
      path: route,
      exact: false,
      strict: false
    });
  }

  const isCurrentRouteAddOns = () => isRouteMatched(ADD_ONS)
    || isRouteMatched(ADD_ON_ORDERS)
    || isRouteMatched(ADD_ON_CREATE)
    || isRouteMatched(ADD_ONS_NEW)
    || isRouteMatched(ADD_ON_EDIT);

  // TODO: Delete old Dashboard in enterprise. Move My Events, Discount Codes, Reports and Add-Ons to host.
  const getSelectedRoute = () => {
    if (isRouteMatched(NEW_DASHBOARD)) {
      return (
        <>
          {selectedRoute(DashboardIcon, formatMessage(locale.dashboard))}
          <NewFeatureBadgeWithExpiry releaseDate={NEW_DASHBOARD_RELEASE_DATE} />
        </>
      );
    }
    else if (isRouteMatched(MY_EVENTS)) return selectedRoute(MyEventsIcon, formatMessage(locale.myEvents));
    else if (isRouteMatched(DISCOUNT_CODES)) return selectedRoute(GDCIcon, formatMessage(locale.global_discount_codes));
    else if (isCurrentRouteAddOns()) {
      return selectedRoute(AddOnsIcon, formatMessage(locale.add_ons));
    }
    else if (isRouteMatched(FEES)) return selectedRoute(FeesIcon, formatMessage(locale.fees));
    return "";
  };

  return (
    isDesktopLayout ?  renderDesktopLayout() : renderMobileLayout()
  );

  function renderMobileLayout() {
    return (
      <div className={styles.accountNavbarMobileNav}>
        <OrganizationSelect
          {...props}
          currentUser={user}
          enabled={organizationSelectionEnabled}
          selectedMembership={selectedMembership}
          memberships={getMemberships()}
        />
        <div className={styles.accountNavbarSelect}>
          <div className={styles.accountNavbarSelectRow} onClick={() => toggleShowOptions()}>
            {getSelectedRoute()}
            <ChevronIcon className={`${styles.accountNavbarSelectorArrow} ${expanded && styles.expanded}`} />
          </div>
          <div className={`${styles.accountNavbarDropdown}
          ${expanded ? styles.expanded : styles.closed}`}>
            { permissions.hasPermissionForDashboard &&
              <div className={styles.accountNavbarOption}>
                <a href={NEW_DASHBOARD}
                className={isCurrentRouteDashboard() ? styles.accountNavbarActive : styles.accountNavbarInactive}>
                  <div className={styles.accountNavbarIcon}>
                    {isCurrentRouteDashboard() ? <DashboardIconActive /> : <DashboardIcon />}
                  </div>
                  {formatMessage(locale.dashboard)}
                  <div className={styles.accountNavbarAfterIcon}>
                  <NewFeatureBadgeWithExpiry releaseDate={NEW_DASHBOARD_RELEASE_DATE} />
                  </div>
                </a>
              </div>
            }
            <div className={styles.accountNavbarOption}>
              <a href={MY_EVENTS}
                 className={currentRoute === MY_EVENTS ? styles.accountNavbarActive : styles.accountNavbarInactive}>
                <div className={styles.accountNavbarIcon}>
                  {currentRoute === MY_EVENTS ? <MyEventsIconActive/> : <MyEventsIcon/>}
                </div>
                {formatMessage(locale.myEvents)}
              </a>
            </div>
            {selectedUser.id === user.id &&
              props.currencies?.length !== 0 &&
              <div className={styles.accountNavbarOption}>
                <a href={`${BALANCES}/${props.currencies?.[0]}`}
                   className={currentRoute === BALANCES ? styles.accountNavbarActive : styles.accountNavbarInactive}>
                  <div className={styles.accountNavbarIcon}>
                    <BalancesMobileIcon/>
                  </div>
                  {formatMessage(locale.balances)}
                </a>
              </div>
            }
            {permissions.hasPermissionForReports &&
              <div className={styles.accountNavbarOption}
                   onClick={() => handleTrackAmplitude('Account Dashboard Reports: View', { ref: 'dashboard' })}>
                <a href={REPORTS}
                   className={currentRoute === REPORTS ? styles.accountNavbarActive : styles.accountNavbarInactive}>
                  <div className={styles.accountNavbarIcon}>
                    {currentRoute === REPORTS ? <ReportsIconActive/> : <ReportsIcon/>}
                  </div>
                  {formatMessage(locale.reports)}
                </a>
              </div>
            }
            {permissions.hasPermissionForDiscountCodes &&
              <div className={styles.accountNavbarOption}>
                <a href={DISCOUNT_CODES}
                   className={currentRoute === DISCOUNT_CODES ?
                     styles.accountNavbarActive : styles.accountNavbarInactive}>
                  <div className={styles.accountNavbarIcon}>
                    {currentRoute === DISCOUNT_CODES ? <GDCIconActive/> : <GDCIcon/>}
                  </div>
                  {formatMessage(locale.global_discount_codes)}
                </a>
              </div>
            }
            {permissions.hasPermissionForManageAddOns &&
              <div className={styles.accountNavbarOption}>
                <a href={ADD_ONS}
                   className={isCurrentRouteAddOns() ? styles.accountNavbarActive : styles.accountNavbarInactive}>
                  <div className={styles.accountNavbarIcon}>
                    {isCurrentRouteAddOns() ? <AddOnsActiveIcon/> : <AddOnsIcon/>}
                  </div>
                  {formatMessage(locale.add_ons)}
                </a>
              </div>
            }
            {permissions.hasPermissionForFees &&
              <div className={styles.accountNavbarOption}>
                <a href={FEES}
                   className={isCurrentRouteFees() ? styles.accountNavbarActive : styles.accountNavbarInactive}>
                  <div className={styles.accountNavbarIcon}>
                    {isCurrentRouteFees() ? <FeesIconActive/> : <FeesIcon/>}
                  </div>
                  {formatMessage(locale.fees)}
                </a>
              </div>
            }
          </div>
        </div>
      </div>
    )
  }

  function renderDesktopLayout() {
    return (
      <div>
        <div className={styles.accountNavbarContainer}>
          <div className={styles.accountNavbarContent}>
            <OrganizationSelect
              {...props}
              enabled={organizationSelectionEnabled}
              selectedMembership={selectedMembership}
              memberships={getMemberships()}
            />
            <div className={styles.accountNavbarTabs}>
              { permissions.hasPermissionForDashboard &&
                <div className={styles.accountNavbarTab}>
                  <a
                  href={NEW_DASHBOARD}
                  className={currentRoute === NEW_DASHBOARD ? styles.accountNavbarActive : undefined}>
                    {isCurrentRouteDashboard() ? <DashboardIconActive /> : <DashboardIcon />}
                    {formatMessage(locale.dashboard)}
                    <div className={styles.accountNavbarAfterIcon}>
                      <NewFeatureBadgeWithExpiry releaseDate={NEW_DASHBOARD_RELEASE_DATE}/>
                    </div>
                  </a>
                </div>
              }
              <div className={styles.accountNavbarTab}>
                <a href={MY_EVENTS} className={currentRoute === MY_EVENTS ? styles.accountNavbarActive : undefined}>
                  {currentRoute === MY_EVENTS ? <MyEventsIconActive /> : <MyEventsIcon />}
                  {formatMessage(locale.myEvents)}
                </a>
              </div>
              {selectedUser.id === user.id &&
              <div className={styles.accountNavbarDropdownTab}>
                <div
                  className={currentRoute.includes(BALANCES) ?
                  styles.accountNavbarDropdownActive : styles.accountNavbarDropdownTab}
                >
                <HandCoinIcon
                  className={styles.accountNavbarDropdownTabIcon}
                />
                <Dropdown
                  className={styles.accountNavbarDropdownText}
                  text={formatMessage(locale.balances)}
                >
                  <Dropdown.Menu
                    style={{width: '150px', marginLeft: '-45px', marginTop: '5px', padding: '0px 10px 10px 10px'}}
                  >
                    {props.currencies
                    ?.sort((a,b) => a > b ? 1 : -1)
                    .map((currency) => (
                      <Dropdown.Item
                        key={currency}
                        className={styles.accountNavbarDropdownOptions}
                        text={`${currency} (${getSymbolFromCurrency(currency)})`}
                        href={`${Config.api.host}/dashboard/balance/${currency}`}
                      />
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                </div>
              </div>
              }
              {permissions.hasPermissionForManageAddOns &&
                <div className={styles.accountNavbarTab}>
                  <a href={ADD_ONS} className={isCurrentRouteAddOns() ? styles.accountNavbarActive : undefined}>
                    {isCurrentRouteAddOns() ? <AddOnsActiveIcon /> : <AddOnsIcon />}
                    {formatMessage(locale.add_ons)}
                  </a>
                </div>
              }
              {permissions.hasPermissionForReports &&
                <div className={styles.accountNavbarTab}>
                  <a href={REPORTS} className={currentRoute === REPORTS ? styles.accountNavbarActive : undefined}>
                    {currentRoute === REPORTS ? <ReportsIcon /> : <ReportsIconActive />}
                    {formatMessage(locale.reports)}
                  </a>
                </div>
              }
              {permissions.hasPermissionForDiscountCodes &&
                <div className={styles.accountNavbarTab}>
                  <a href={DISCOUNT_CODES}
                  className={currentRoute === DISCOUNT_CODES ? styles.accountNavbarActive : undefined}>
                    {currentRoute === DISCOUNT_CODES ? < GDCIconActive/> : <GDCIcon />}
                    {formatMessage(locale.global_discount_codes)}
                  </a>
                </div>
              }
              {permissions.hasPermissionForFees &&
                <div className={styles.accountNavbarTab}>
                  <a href={FEES}
                     className={isCurrentRouteFees() ? styles.accountNavbarActive : undefined}>
                    {isCurrentRouteFees() ? <FeesIconActive/> : <FeesIcon/>}
                    {formatMessage(locale.fees)}
                  </a>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(withRouter(AccountNavbar));
